import { useState, useRef, useEffect, forwardRef } from 'react';

import classNames from 'classnames';
import { bowser } from '../../../global/browser-support';

import styles from './text-input-comp.module.scss';
import type { ValidationDefinition } from '../../../global/hooks/validation/types';
import { textLengthLessThanOrEqualTo } from '../../../global/hooks/validation/validators/text-length-less-than-or-equal-to';
import { useValidation } from '../../../global/hooks/validation/use-validation';
import { ValidationErrorTooltip } from '../validation-error-tooltip/validation-error-tooltip';
import ComponentAnimation, {
  AnimationTypes,
} from '../../general/component-animation/component-animation';
import CTATooltip from '../../general/cta-tooltip/cta-tooltip';
import PadlockFilledIcon from '../../general/icon/components/padlock-filled';
import ErrorCircleIcon from '../../general/icon/components/error-circle';
import TickCircleIcon from '../../general/icon/components/tick-circle';
import { HTMLInputTypeAttribute } from 'react';

export type InputProps = {
  autocomplete?: string;
  focusInput?: boolean;
  className?: string;
  getMetaText?: (value: string) => string | null | undefined;
  hardLimitOnMaximumCharacters?: boolean;
  id: string;
  isReadOnly?: boolean;
  isDisabled?: boolean;
  showIcon?: boolean;
  parentErrorMessage?: string | null | undefined;
  hasParentError?: boolean;
  hasSuccessTick?: boolean;
  smallMarginBottom?: boolean;
  ellipsifyExpandedLabel?: boolean;
  max?: number;
  min?: number;
  label?: string;
  reducedLabel?: string;
  maxCharacters?: number;
  onBlur?: () => void;
  onClick?: () => void;
  onChange?: (
    value: string,
  ) => undefined | Promise<undefined> | Promise<void> | void;
  type?: HTMLInputTypeAttribute;
  value?: string;
  tabIndex?: number;
  exactValue?: string;
  isRequired?: boolean;
};

type Tooltip = {
  isVisible?: boolean;
  text?: string;
};

const TextInputField = (
  {
    autocomplete,
    focusInput,
    getMetaText,
    hardLimitOnMaximumCharacters,
    id,
    isReadOnly = false,
    isDisabled = false,
    showIcon = true,
    parentErrorMessage,
    hasParentError,
    hasSuccessTick,
    smallMarginBottom,
    ellipsifyExpandedLabel,
    max,
    min,
    label,
    reducedLabel,
    maxCharacters,
    onBlur = () => undefined,
    onChange = () => undefined,
    onClick = () => undefined,
    type,
    value,
    tabIndex,
    exactValue,
    isRequired,
    className,
  }: InputProps,
  forwardedRef,
) => {
  const inputValidation: ValidationDefinition<string | null | undefined> = {
    validators: [],
  };

  if (maxCharacters && maxCharacters > 0) {
    inputValidation.validators.push({
      validator: textLengthLessThanOrEqualTo(maxCharacters),
      invalidClassName: styles['input--invalid'],
      invalidErrorMessage: polyglot.t('input_validation.character_limit'),
    });
  }

  const getClippedValue = (value?: string | null): string => {
    if (value && maxCharacters && hardLimitOnMaximumCharacters) {
      return value.substring(0, maxCharacters);
    }
    return value || '';
  };

  const clippedValue = getClippedValue(value);

  const [
    isValid,
    validationClassNames,
    errorMessage,
    informValidationOfValueChange,
  ] = useValidation<string>(inputValidation);

  const [inputIsFocused, setInputIsFocused] = useState<boolean>(false);
  const [inputIsAutofilled, setInputIsAutofilled] = useState<boolean>(false);
  const [shouldFocusInput, setShouldFocusInput] =
    useState<boolean>(!!focusInput);
  const [currentValue, setCurrentValue] = useState<string>(clippedValue);
  const [tooltip, setTooltip] = useState<Tooltip>({});

  const textInputField = useRef<HTMLInputElement | null>(null);

  const shouldReduceLabel =
    exactValue || currentValue || inputIsFocused || inputIsAutofilled;

  const focusElement = () => {
    if (textInputField.current && shouldFocusInput) {
      textInputField.current.focus();
    }
  };

  const showTooltip = e => {
    e.preventDefault();
    setTooltip({
      isVisible: true,
      text: isReadOnly
        ? polyglot.t('components.text_input_comp.preset_field')
        : '',
    });
  };

  const hideTooltip = () => {
    setTooltip({});
  };

  useEffect(() => {
    setShouldFocusInput(!!focusInput);
  }, [focusInput]);

  useEffect(() => {
    focusElement();
  });

  const metaText =
    getMetaText && typeof getMetaText === 'function'
      ? getMetaText(currentValue)
      : null;

  const { isMobile } = context.viewport;

  useEffect(() => {
    // Fix for a chrome specific bug with autofilled fields that use the floating label
    // style design that we use on the login page where a user can autofill their
    // username and password (here you can see this problem on google login page:
    // https://github.com/Baedda/floating-form-labels/issues/12#issuecomment-502006732),
    // @ts-ignore causes error in Zeus though not Storyboook
    if (bowser.chrome) {
      const chromeAutofillBugCheckTimeLimit = 3000;
      const chromeAutofillBugFixLoopDuration = 50;

      const chromeAutofillBugInterval = setInterval(() => {
        const input = document.querySelector(`input#${id}`);

        let hasAutofillClass = false;

        if (input) {
          hasAutofillClass = input.matches(':-webkit-autofill');
        }

        if (hasAutofillClass) {
          setInputIsAutofilled(true);

          clearInterval(chromeAutofillBugInterval);
        }
      }, chromeAutofillBugFixLoopDuration);

      setTimeout(() => {
        clearInterval(chromeAutofillBugInterval);
      }, chromeAutofillBugCheckTimeLimit);
    }
  }, []);

  const shouldHighlightValid =
    hasSuccessTick &&
    (exactValue || currentValue) &&
    !parentErrorMessage &&
    !hasParentError;

  return (
    <div
      className={classNames(
        styles['input'],
        isDisabled && styles['input--disabled-indication'],
        shouldReduceLabel && label && styles['input--reduced'],
        inputIsFocused && styles['input--focused'],
        (parentErrorMessage || hasParentError) && styles['input--invalid'],
        shouldHighlightValid && styles['input--valid'],
        (parentErrorMessage || hasParentError) &&
          smallMarginBottom &&
          styles['input--invalid-small-margin-bottom'],
        validationClassNames,
        className,
      )}
    >
      {label && (
        <>
          <label
            htmlFor={id}
            className={classNames(
              styles['input__label'],
              styles['input__label--expanded'],
              ellipsifyExpandedLabel &&
                styles['input__label--expanded-ellipsified'],
            )}
          >
            {label}
          </label>
          <label
            className={classNames(
              styles['input__label'],
              styles['input__label--shrunk'],
            )}
          >
            {shouldReduceLabel && reducedLabel ? reducedLabel : label}
          </label>
        </>
      )}

      <input
        autoComplete={autocomplete}
        className={classNames(styles['input__input'], 'no-focus')}
        id={id}
        ref={forwardedRef || textInputField}
        type={type}
        max={max}
        min={min}
        value={exactValue !== undefined ? exactValue : currentValue}
        tabIndex={tabIndex || (isDisabled || isReadOnly ? -1 : 0)}
        onBlur={() => {
          if (!isDisabled && !isReadOnly) setInputIsFocused(false);
          if (inputIsAutofilled) setInputIsAutofilled(false);
          onBlur();
        }}
        onChange={(evt: React.ChangeEvent<HTMLInputElement>) => {
          const {
            target: { value: eventVal },
          } = evt;
          const value = getClippedValue(eventVal);
          if (exactValue === undefined) {
            setCurrentValue(value);
          }
          informValidationOfValueChange(value);
          onChange(value);
        }}
        onFocus={() => {
          if (!isDisabled && !isReadOnly) setInputIsFocused(true);
          setShouldFocusInput(false);
        }}
        readOnly={isReadOnly || isDisabled}
        aria-required={isRequired ? true : false}
        onClick={onClick}
      />
      {(isReadOnly || parentErrorMessage) && showIcon && (
        <div className={styles['input__icon']}>
          {isReadOnly && (
            <div
              onMouseEnter={!isMobile ? showTooltip : undefined}
              onMouseLeave={!isMobile ? hideTooltip : undefined}
              onClick={isMobile ? showTooltip : undefined}
            >
              <PadlockFilledIcon />
            </div>
          )}
          {(parentErrorMessage || hasParentError) && (
            <div className={styles['input__icon--error']}>
              <ErrorCircleIcon />
            </div>
          )}
          {tooltip.isVisible && (
            <div className={styles['input__icon--tooltip']}>
              <CTATooltip
                data={{
                  title: tooltip.text ? tooltip.text : '',
                }}
                arrowClassName='cta-tooltip__arrow--text-input-comp'
                hasCenteredText
                dismissTooltip={hideTooltip}
                isSmall
                animationDuration={150}
              />
            </div>
          )}
        </div>
      )}
      {shouldHighlightValid && (
        <div className={styles['input__icon']}>
          <div className={styles['input__icon--valid']}>
            <TickCircleIcon width={20} height={20} />
          </div>
        </div>
      )}
      <ComponentAnimation
        displayContentIf={!!metaText}
        type={AnimationTypes.EXPAND_DOWN_AND_FADE_IN}
        id='input-meta-text-animation'
      >
        <div className={styles['input__meta-text']}>{metaText}</div>
      </ComponentAnimation>
      <div className={styles['input__validation-error']}>
        <ValidationErrorTooltip
          message={parentErrorMessage || errorMessage}
          visible={!!parentErrorMessage || (!!errorMessage && !isValid)}
        />
      </div>
    </div>
  );
};

TextInputField.displayName = 'TextInputField';

export default forwardRef<HTMLInputElement, InputProps>(TextInputField);
